import { useMemo } from "react";
import type { Answers, PotentialSection } from "../../types";

export type ChipTone = "solid" | "plain" | "warn" | "ok";
export interface Chip {
  label: string;
  tone: ChipTone;
}
export type Stage = 1 | 2 | 3 | 4 | 5;
export type EffortBand = 1 | 2 | 3 | 4;

export interface Potential {
  /** Company once given, otherwise "Live". */
  title: string;
  /** Reifegrad after the five-stage model of the Agentic BI Guide (chapter ki-readiness). */
  stage: { scored: boolean; level: Stage | null; name: string };
  landscape: Chip[];
  governance: Chip[];
  ai: Chip[];
  path: { label: string; detail: string } | null;
  effort: { scored: boolean; band: EffortBand | null; bandName: string; score: number };
  /** The one next step the guide names for the current stage. */
  nextStep: string;
}

/** Same wording as the guide chapter "Von Dashboards ohne Doku bis agentenfähig". */
const STAGE_NAMES = [
  "Dashboards ohne Doku",
  "Kennzahlen beschrieben",
  "Lineage nachvollziehbar",
  "Regelsatz & Tests",
  "Agentenfähig",
];
const STAGE_NEXT = [
  "Kennzahlen-Katalog und Metadaten-Inventur: erst zählen, dann beschreiben.",
  "Lineage-Graph aufbauen: welche Spalte welchen Bericht speist.",
  "Regelsatz definieren und Tests vor jede Modelländerung legen.",
  "Ersten Agenten pilotieren: Dokumentation und Reviews mit Human-in-the-Loop.",
  "Skalieren: Agenten im Betrieb, als Managed Service oder mit eigener Lizenz.",
];
const BAND_NAMES = ["Basis", "Standard", "Erweitert", "Komplex"];
const LEGACY = new Set(["sas", "crystal", "sapbo", "qlik", "ssrs"]);
const REGULATED = new Set(["dsgvo_besonders", "gxp", "bafin", "enwg"]);

export function buildPotential(sections: PotentialSection[], answers: Answers): Potential {
  const labelIndex = new Map<string, Map<string, string>>();
  for (const section of sections) {
    for (const question of section.questions) {
      if (question.options) {
        labelIndex.set(question.id, new Map(question.options.map((o) => [o.value, o.label])));
      }
    }
  }
  const single = (id: string): string => (typeof answers[id] === "string" ? (answers[id] as string) : "");
  const multi = (id: string): string[] => (Array.isArray(answers[id]) ? (answers[id] as string[]) : []);
  const known = (id: string, value: string): boolean => labelIndex.get(id)?.has(value) ?? false;
  const label = (id: string, value: string): string => labelIndex.get(id)?.get(value) ?? value;
  const chip = (text: string, tone: ChipTone = "solid"): Chip => ({ label: text, tone });

  // ── Reifegrad: each stage requires the one below it, like the guide's Prüffragen ──
  const kennzahlen = single("kennzahlen");
  const lineage = single("lineage");
  const tests = single("tests");
  const kiEinsatz = single("ki_einsatz");
  const stageScored = Boolean(kennzahlen || lineage || tests || kiEinsatz);
  let level: Stage = 1;
  if (kennzahlen === "dokumentiert") level = 2;
  if (level >= 2 && lineage === "ja") level = 3;
  if (level >= 3 && tests === "ja") level = 4;
  if (level >= 4 && kiEinsatz === "produktiv") level = 5;

  // ── Landschaft ──
  const systems = multi("quellsysteme").filter((v) => known("quellsysteme", v));
  const landscape: Chip[] = systems.map((v) => chip(label("quellsysteme", v)));
  const berichte = single("berichte");
  const quellen = single("quellen");
  const nutzer = single("nutzer");
  if (berichte) landscape.push(chip(`Berichte: ${label("berichte", berichte)}`, "plain"));
  if (quellen) landscape.push(chip(`Quellen: ${label("quellen", quellen)}`, "plain"));
  if (nutzer) landscape.push(chip(`Leser: ${label("nutzer", nutzer)}`, "plain"));

  // ── Governance ──
  const governance: Chip[] = [];
  const berechtigungen = single("berechtigungen");
  if (berechtigungen) {
    governance.push(
      chip(label("berechtigungen", berechtigungen), berechtigungen === "rollenmodell" ? "ok" : berechtigungen === "adhoc" ? "warn" : "plain")
    );
  }
  const regulierung = multi("regulierung").filter((v) => known("regulierung", v));
  for (const v of regulierung) governance.push(chip(label("regulierung", v), v === "keine" ? "plain" : "solid"));
  const residenz = single("datenresidenz");
  if (residenz === "lokal_pflicht") governance.push(chip("Local-First erforderlich", "warn"));
  else if (residenz) governance.push(chip(label("datenresidenz", residenz), "plain"));

  // ── KI ──
  const ai: Chip[] = [];
  if (kiEinsatz) ai.push(chip(`KI heute: ${label("ki_einsatz", kiEinsatz)}`, kiEinsatz === "produktiv" ? "ok" : "plain"));
  const metadaten = single("metadaten_export");
  if (metadaten) ai.push(chip(metadaten === "ja" ? "Metadaten exportierbar" : metadaten === "nein" ? "Metadaten-Export fehlt" : "Metadaten-Export unklar", metadaten === "ja" ? "ok" : metadaten === "nein" ? "warn" : "plain"));
  for (const v of multi("ziel_ki").filter((x) => known("ziel_ki", x))) ai.push(chip(label("ziel_ki", v)));

  // ── Pfad ──
  const vorhaben = single("vorhaben");
  const plattform = single("zielplattform");
  const zeit = single("zeithorizont");
  let path: Potential["path"] = null;
  if (vorhaben || plattform) {
    const what = vorhaben ? label("vorhaben", vorhaben) : "Vorhaben offen";
    const where = plattform ? (plattform === "offen" ? "Plattform noch offen" : `nach ${label("zielplattform", plattform)}`) : "";
    const regulated = residenz === "lokal_pflicht" || regulierung.some((v) => REGULATED.has(v));
    const detail = regulated
      ? "Local-First: Metadaten statt Echtdaten, Analyse auf dem Gerät des Beraters."
      : zeit === "sofort"
        ? "Start kurzfristig möglich: Metadaten-Inventur in Woche eins."
        : plattform === "offen"
          ? "Die Inventur liefert die Entscheidungsgrundlage für die Plattform."
          : "";
    path = { label: [what, where].filter(Boolean).join(" "), detail };
  }

  // ── Aufwandsband ──
  let score = 0;
  let scored = false;
  const berichtePoints: Record<string, number> = { bis_20: 0, "21_80": 1, "81_200": 2, ueber_200: 3 };
  const quellenPoints: Record<string, number> = { "1": 0, "2_3": 1, "4_6": 2, ueber_6: 3 };
  const nutzerPoints: Record<string, number> = { bis_20: 0, "21_100": 0, "101_500": 1, ueber_500: 2 };
  if (berichte) { score += berichtePoints[berichte] ?? 0; scored = true; }
  if (quellen) { score += quellenPoints[quellen] ?? 0; scored = true; }
  if (nutzer) { score += nutzerPoints[nutzer] ?? 0; scored = true; }
  if (systems.length > 0) {
    scored = true;
    if (systems.some((v) => LEGACY.has(v))) score += 1;
    if (systems.length >= 3) score += 1;
  }
  if (berechtigungen === "adhoc") score += 1;
  if (regulierung.some((v) => REGULATED.has(v))) score += 1;
  if (single("doku") === "keine") score += 1;
  const band: EffortBand | null = !scored ? null : score <= 2 ? 1 : score <= 5 ? 2 : score <= 8 ? 3 : 4;

  const company = typeof answers.unternehmen === "string" ? answers.unternehmen.trim() : "";

  return {
    title: company || "Live",
    stage: { scored: stageScored, level: stageScored ? level : null, name: stageScored ? STAGE_NAMES[level - 1] : "—" },
    landscape,
    governance,
    ai,
    path,
    effort: { scored, band, bandName: band ? BAND_NAMES[band - 1] : "—", score },
    nextStep: stageScored ? STAGE_NEXT[level - 1] : "Beantworten Sie Abschnitt 02 – daraus folgt der nächste Schritt.",
  };
}

/** Memoised view of {@link buildPotential} for the form. */
export function usePotential(sections: PotentialSection[], answers: Answers): Potential {
  return useMemo(() => buildPotential(sections, answers), [sections, answers]);
}
